/**
 * Godot's tonemap pass as one `GodotToneMapEffect` for a viewport: built from the Environment and
 * the camera attributes its world renders with, with the glow map bound once it loads. The root
 * canvas's compositor and a SubViewport's pass each own one.
 */
import { useEffect, useMemo } from 'react';
import { projectGlowUsesBicubicUpscale } from '../../parser/projectSettingsParser';
import { useProjectSettings } from '../contexts/ProjectSettingsContext';
import { useTextureView } from '../useTextureView';
import { useDisposeOnRelease } from '../useDisposeOnRelease';
import { GLOW_MAP_SAMPLER, GodotToneMapEffect } from './GodotToneMapEffect';
/** The effect for `inputs`, rebuilt when one changes. The hook owns the effect and disposes it. */
export function useGodotToneMapEffect(inputs) {
    const { glow, adjustments, toneMapping, autoExposure, transparentBackground, usesDebanding } = inputs;
    const { mode, white, exposure, agxContrast } = toneMapping;
    const glowMap = useTextureView(inputs.glowMap, GLOW_MAP_SAMPLER);
    const usesBicubicGlowUpscale = projectGlowUsesBicubicUpscale(useProjectSettings().settings);
    const effect = useMemo(() => new GodotToneMapEffect({
        glow,
        adjustments,
        autoExposure,
        toneMapping: { mode, white, agxContrast },
        exposure,
        transparentBackground,
        usesBicubicGlowUpscale,
        usesDebanding,
    }), [
        glow,
        adjustments,
        autoExposure,
        mode,
        white,
        exposure,
        agxContrast,
        transparentBackground,
        usesBicubicGlowUpscale,
        usesDebanding,
    ]);
    useDisposeOnRelease(effect);
    // A uniform, not an effect rebuild: the map's arrival changes no shader.
    useEffect(() => {
        if (glowMap)
            effect.setGlowMap(glowMap);
        else
            effect.clearGlowMap();
    }, [effect, glowMap]);
    return effect;
}
//# sourceMappingURL=useGodotToneMapEffect.js.map